import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Stack
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Receipt as InvoiceIcon,
  Payment as PaymentIcon,
  DateRange as DateIcon,
  AccountBalance as BankIcon,
  Analytics as AnalyticsIcon,
  Assessment as ReportIcon,
  Group as ClientIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  FilterList as FilterIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Data Interfaces
interface RevenueStats {
  totalRevenue: number;
  monthlyRevenue: number;
  yearlyRevenue: number;
  averageProjectValue: number;
  totalInvoices: number;
  paidInvoices: number;
  pendingInvoices: number;
  overdueInvoices: number;
  collectionRate: number;
  monthlyGrowth: number;
  yearlyGrowth: number;
  activeClients: number;
  newClients: number;
}

interface MonthlyRevenue {
  month: string;
  revenue: number;
  invoices: number;
  clients: number;
  growth: number;
}

interface ClientRevenue {
  clientId: number;
  clientName: string;
  totalRevenue: number;
  invoiceCount: number;
  avgProjectValue: number;
  lastPayment: string;
  status: 'active' | 'inactive';
  industry: string;
}

interface RevenueByService {
  service: string;
  revenue: number;
  percentage: number;
  projects: number;
  growth: number;
}

interface PaymentMethod {
  method: string;
  amount: number;
  percentage: number;
  count: number;
}

// Mock Data
const MOCK_REVENUE_STATS: RevenueStats = {
  totalRevenue: 1547250,
  monthlyRevenue: 178900,
  yearlyRevenue: 1547250,
  averageProjectValue: 28450,
  totalInvoices: 156,
  paidInvoices: 142,
  pendingInvoices: 8,
  overdueInvoices: 6,
  collectionRate: 91.0,
  monthlyGrowth: 12.5,
  yearlyGrowth: 34.2,
  activeClients: 48,
  newClients: 7
};

const MOCK_MONTHLY_REVENUE: MonthlyRevenue[] = [
  { month: 'Jan 2024', revenue: 124500, invoices: 15, clients: 12, growth: 8.2 },
  { month: 'Feb 2024', revenue: 136750, invoices: 18, clients: 14, growth: 9.8 },
  { month: 'Mar 2024', revenue: 145600, invoices: 16, clients: 13, growth: 6.5 },
  { month: 'Apr 2024', revenue: 128900, invoices: 14, clients: 11, growth: -11.5 },
  { month: 'May 2024', revenue: 167800, invoices: 22, clients: 18, growth: 30.2 },
  { month: 'Jun 2024', revenue: 154300, invoices: 19, clients: 16, growth: -8.0 },
  { month: 'Jul 2024', revenue: 189750, invoices: 24, clients: 20, growth: 23.0 },
  { month: 'Aug 2024', revenue: 201450, invoices: 26, clients: 22, growth: 6.2 },
  { month: 'Sep 2024', revenue: 178900, invoices: 21, clients: 17, growth: -11.2 },
  { month: 'Oct 2024', revenue: 219250, invoices: 28, clients: 24, growth: 22.5 }
];

const MOCK_CLIENT_REVENUE: ClientRevenue[] = [
  {
    clientId: 1,
    clientName: 'TechFlow Solutions',
    totalRevenue: 234500,
    invoiceCount: 18,
    avgProjectValue: 13028,
    lastPayment: '2024-10-15',
    status: 'active',
    industry: 'Technology'
  },
  {
    clientId: 2,
    clientName: 'Global Industries Corp',
    totalRevenue: 189750,
    invoiceCount: 12,
    avgProjectValue: 15813,
    lastPayment: '2024-10-12',
    status: 'active',
    industry: 'Manufacturing'
  },
  {
    clientId: 3,
    clientName: 'Digital Marketing Plus',
    totalRevenue: 156800,
    invoiceCount: 22,
    avgProjectValue: 7127,
    lastPayment: '2024-10-08',
    status: 'active',
    industry: 'Marketing'
  },
  {
    clientId: 4,
    clientName: 'FinanceFirst Solutions',
    totalRevenue: 145600,
    invoiceCount: 8,
    avgProjectValue: 18200,
    lastPayment: '2024-09-28',
    status: 'active',
    industry: 'Financial'
  },
  {
    clientId: 5,
    clientName: 'HealthTech Innovations',
    totalRevenue: 123450,
    invoiceCount: 14,
    avgProjectValue: 8818,
    lastPayment: '2024-10-10',
    status: 'active',
    industry: 'Healthcare'
  }
];

const MOCK_REVENUE_BY_SERVICE: RevenueByService[] = [
  { service: 'Web Development', revenue: 467850, percentage: 30.2, projects: 28, growth: 15.8 },
  { service: 'Mobile App Development', revenue: 387900, percentage: 25.1, projects: 18, growth: 22.4 },
  { service: 'UI/UX Design', revenue: 232650, percentage: 15.0, projects: 35, growth: 8.7 },
  { service: 'DevOps & Infrastructure', revenue: 201350, percentage: 13.0, projects: 12, growth: 34.2 },
  { service: 'Consulting & Strategy', revenue: 154725, percentage: 10.0, projects: 22, growth: -5.3 },
  { service: 'Maintenance & Support', revenue: 102775, percentage: 6.6, projects: 45, growth: 12.1 }
];

const MOCK_PAYMENT_METHODS: PaymentMethod[] = [
  { method: 'Bank Transfer', amount: 856750, percentage: 55.4, count: 89 },
  { method: 'Credit Card', amount: 387900, percentage: 25.1, count: 45 },
  { method: 'PayPal', amount: 201350, percentage: 13.0, count: 18 },
  { method: 'Check', amount: 101250, percentage: 6.5, count: 4 }
];

const RevenueAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<RevenueStats>(MOCK_REVENUE_STATS);
  const [monthlyData, setMonthlyData] = useState<MonthlyRevenue[]>(MOCK_MONTHLY_REVENUE);
  const [clientRevenue, setClientRevenue] = useState<ClientRevenue[]>(MOCK_CLIENT_REVENUE);
  const [serviceRevenue, setServiceRevenue] = useState<RevenueByService[]>(MOCK_REVENUE_BY_SERVICE);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(MOCK_PAYMENT_METHODS);
  const [selectedTab, setSelectedTab] = useState(0);
  const [dateRange, setDateRange] = useState('year');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const token = localStorage.getItem('auth_token');
      
      // Calculate start and end dates based on dateRange
      const endDate = new Date();
      const startDate = new Date();
      
      switch (dateRange) {
        case 'month':
          startDate.setMonth(startDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(startDate.getMonth() - 3);
          break;
        case 'year':
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
        default:
          startDate.setFullYear(startDate.getFullYear() - 1);
      }
      
      const params = new URLSearchParams({
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
      });
      
      const response = await fetch(`${API_BASE_URL}/admin/financial/analytics?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to load analytics');
      }
      
      const data = await response.json();
      // The component already has mock data, so this will populate when backend returns data
      console.log('Analytics data:', data);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const getGrowthIcon = (growth: number) => {
    return growth >= 0 ? <ArrowUpIcon color="success" /> : <ArrowDownIcon color="error" />;
  };

  const renderOverviewTab = () => (
    <Box>
      {/* Revenue Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <MoneyIcon sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
                <Typography variant="h4" color="primary.main" fontWeight={700}>
                  {formatCurrency(stats.totalRevenue)}
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                Total Revenue
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                {getGrowthIcon(stats.yearlyGrowth)}
                <Typography variant="body2" color={stats.yearlyGrowth >= 0 ? 'success.main' : 'error.main'}>
                  {formatPercentage(stats.yearlyGrowth)} YoY
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mr: 1 }} />
                <Typography variant="h4" color="success.main" fontWeight={700}>
                  {formatCurrency(stats.monthlyRevenue)}
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                This Month
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                {getGrowthIcon(stats.monthlyGrowth)}
                <Typography variant="body2" color={stats.monthlyGrowth >= 0 ? 'success.main' : 'error.main'}>
                  {formatPercentage(stats.monthlyGrowth)} MoM
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <InvoiceIcon sx={{ fontSize: 40, color: 'info.main', mr: 1 }} />
                <Typography variant="h4" color="info.main" fontWeight={700}>
                  {stats.collectionRate.toFixed(1)}%
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                Collection Rate
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {stats.paidInvoices}/{stats.totalInvoices} invoices paid
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <ClientIcon sx={{ fontSize: 40, color: 'warning.main', mr: 1 }} />
                <Typography variant="h4" color="warning.main" fontWeight={700}>
                  {stats.activeClients}
                </Typography>
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                Active Clients
              </Typography>
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                +{stats.newClients} new this month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Revenue Trend */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AnalyticsIcon />
            Monthly Revenue Trend
          </Typography>
          
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="right">Invoices</TableCell>
                  <TableCell align="right">Clients</TableCell>
                  <TableCell align="right">Growth</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyData.slice(-6).map((month) => (
                  <TableRow key={month.month}>
                    <TableCell>{month.month}</TableCell>
                    <TableCell align="right">{formatCurrency(month.revenue)}</TableCell>
                    <TableCell align="right">{month.invoices}</TableCell>
                    <TableCell align="right">{month.clients}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {getGrowthIcon(month.growth)}
                        <Typography 
                          variant="body2" 
                          color={month.growth >= 0 ? 'success.main' : 'error.main'}
                          sx={{ ml: 0.5 }}
                        >
                          {formatPercentage(month.growth)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Invoice Status Overview */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InvoiceIcon />
                Invoice Status
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Paid Invoices</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {stats.paidInvoices} ({((stats.paidInvoices / stats.totalInvoices) * 100).toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(stats.paidInvoices / stats.totalInvoices) * 100}
                  color="success"
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Pending Invoices</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {stats.pendingInvoices} ({((stats.pendingInvoices / stats.totalInvoices) * 100).toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(stats.pendingInvoices / stats.totalInvoices) * 100}
                  color="warning"
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Overdue Invoices</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {stats.overdueInvoices} ({((stats.overdueInvoices / stats.totalInvoices) * 100).toFixed(1)}%)
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(stats.overdueInvoices / stats.totalInvoices) * 100}
                  color="error"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaymentIcon />
                Payment Methods
              </Typography>
              
              <List dense>
                {paymentMethods.map((method, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <BankIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={method.method}
                      secondary={`${method.count} transactions`}
                    />
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(method.amount)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {method.percentage.toFixed(1)}%
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderClientsTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ClientIcon />
        Revenue by Client
      </Typography>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Industry</TableCell>
              <TableCell align="right">Total Revenue</TableCell>
              <TableCell align="right">Invoices</TableCell>
              <TableCell align="right">Avg Project Value</TableCell>
              <TableCell align="right">Last Payment</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clientRevenue.map((client) => (
              <TableRow key={client.clientId} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                      {client.clientName.charAt(0)}
                    </Avatar>
                    <Typography variant="body2" fontWeight={600}>
                      {client.clientName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{client.industry}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatCurrency(client.totalRevenue)}
                </TableCell>
                <TableCell align="right">{client.invoiceCount}</TableCell>
                <TableCell align="right">{formatCurrency(client.avgProjectValue)}</TableCell>
                <TableCell align="right">{client.lastPayment}</TableCell>
                <TableCell align="center">
                  <Chip 
                    label={client.status} 
                    color={getStatusColor(client.status) as any}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderServicesTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon />
        Revenue by Service
      </Typography>
      
      <Grid container spacing={3}>
        {serviceRevenue.map((service, index) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={index}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {service.service}
                </Typography>
                
                <Typography variant="h4" color="primary.main" fontWeight={700} gutterBottom>
                  {formatCurrency(service.revenue)}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {service.percentage.toFixed(1)}% of total
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {service.projects} projects
                  </Typography>
                </Box>
                
                <LinearProgress 
                  variant="determinate" 
                  value={service.percentage}
                  sx={{ mb: 2, height: 8, borderRadius: 1 }}
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getGrowthIcon(service.growth)}
                  <Typography 
                    variant="body2" 
                    color={service.growth >= 0 ? 'success.main' : 'error.main'}
                  >
                    {formatPercentage(service.growth)} vs last period
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AnalyticsIcon fontSize="large" />
          Revenue Analytics
        </Typography>
        
        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={dateRange}
              label="Period"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="quarter">This Quarter</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAnalytics}
            disabled={loading}
          >
            Refresh
          </Button>
          
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
          >
            Export Report
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
          <Tab label="Overview" />
          <Tab label="By Client" />
          <Tab label="By Service" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ mt: 3 }}>
        {selectedTab === 0 && renderOverviewTab()}
        {selectedTab === 1 && renderClientsTab()}
        {selectedTab === 2 && renderServicesTab()}
      </Box>
    </Box>
  );
};

export default RevenueAnalyticsPage;